import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'
import { useAuth } from '@/hooks/useAuth'
import { useCampaignTotpStepUp } from '@/contexts/CampaignTotpStepUpContext'
import { canManageInstitutionCampaigns } from '@/lib/campaign-totp-step-up'
import {
  fetchCombos,
  createCombo,
  updateCombo,
  deleteCombo,
  formatComboPrice,
  calculateComboFullPriceFromCampaignPrice,
} from '@/lib/combos'
import { buildComboOnlyWidgetSnippet, type WidgetEmbedSnippetFields } from '@/lib/widgetEmbedSnippet'
import type { Combo, ComboInput } from '@/types/combo'
import { CurrencyInput } from '@/components/CurrencyInput'
import styles from './CombosManager.module.css'

type ComboFilter = 'active' | 'inactive'

const EMPTY_WIDGET_SNIPPET_FIELDS: WidgetEmbedSnippetFields = {
  widget_embed_button_text: '',
  widget_embed_button_color: '',
  widget_embed_button_text_color: '',
  widget_embed_button_radius_px: '',
}

interface CombosManagerProps {
  campaignId: number
  campaignPrice?: number | string | null
  /** Campos do botão do widget da campanha (aba Widget), usados no snippet de embed por combo. */
  widgetSnippetFields?: WidgetEmbedSnippetFields
  /** Cor do botão da identidade da campanha (fallback das cores do snippet). */
  landingButtonColor?: string
}

export function CombosManager({
  campaignId,
  campaignPrice,
  widgetSnippetFields,
  landingButtonColor,
}: CombosManagerProps) {
  const { token, guard, isCollaborator } = useAuth()
  const { runWithStepUp } = useCampaignTotpStepUp()
  const [combos, setCombos] = useState<Combo[]>([])
  const [filterStatus, setFilterStatus] = useState<ComboFilter>('active')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingCombo, setEditingCombo] = useState<Combo | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [copiedComboId, setCopiedComboId] = useState<number | null>(null)

  const snippetFields = widgetSnippetFields ?? EMPTY_WIDGET_SNIPPET_FIELDS
  const snippetLandingColor = landingButtonColor ?? '#5e17eb'

  const canEdit = canManageInstitutionCampaigns(guard, Boolean(isCollaborator))

  const filteredCombos = useMemo(() => {
    if (filterStatus === 'active') return combos.filter((c) => c.is_active)
    return combos.filter((c) => !c.is_active)
  }, [combos, filterStatus])

  const loadCombos = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const result = await fetchCombos(campaignId)
      setCombos(result)
    } catch {
      setError('Não foi possível carregar os combos.')
    } finally {
      setIsLoading(false)
    }
  }, [campaignId])

  useEffect(() => {
    loadCombos()
  }, [loadCombos])

  const handleOpenCreateModal = () => {
    setEditingCombo(null)
    setIsModalOpen(true)
  }

  const handleOpenEditModal = (combo: Combo) => {
    setEditingCombo(combo)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingCombo(null)
  }

  const handleSubmit = async (data: ComboInput) => {
    if (!token) {
      setError('Você precisa estar autenticado para realizar esta ação.')
      return
    }

    setIsSubmitting(true)
    setError(null)
    try {
      if (editingCombo) {
        await runWithStepUp(() => updateCombo(editingCombo.id, data, token))
      } else {
        await runWithStepUp(() => createCombo(campaignId, data, token))
      }
      await loadCombos()
      handleCloseModal()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Não foi possível salvar o combo.'
      setError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (comboId: number) => {
    if (!token) return

    if (!window.confirm('Tem certeza que deseja excluir este combo?')) {
      return
    }

    setError(null)

    try {
      await runWithStepUp(() => deleteCombo(comboId, token))
      await loadCombos()
    } catch {
      setError('Não foi possível excluir o combo.')
    }
  }

  const handlePatchCombo = async (comboId: number, patch: Partial<ComboInput>, message: string) => {
    if (!token) return

    try {
      await runWithStepUp(() => updateCombo(comboId, patch, token))
      await loadCombos()
    } catch {
      setError(message)
    }
  }

  return (
    <section className={styles.combosSection}>
      <div className={styles.combosHeader}>
        <div>
          <h3 className={styles.combosTitle}>Combos da Campanha</h3>
          <p className={styles.combosSubtitle}>
            Gerencie posição, favorito, visibilidade e status de cada combo
          </p>
        </div>
        {canEdit && (
          <button className={styles.addComboBtn} type="button" onClick={handleOpenCreateModal}>
            <Icon icon="mdi:plus" />
            Novo Combo
          </button>
        )}
      </div>

      {error && (
        <div className={styles.errorBanner} role="alert">
          <Icon icon="mdi:alert-circle" />
          {error}
        </div>
      )}

      {combos.length > 0 && (
        <div className={styles.combosFilter} role="group" aria-label="Filtrar combos">
          <button
            type="button"
            className={`${styles.combosFilterBtn} ${filterStatus === 'active' ? styles.combosFilterBtnActive : ''}`}
            onClick={() => setFilterStatus('active')}
          >
            Ativos
          </button>
          <button
            type="button"
            className={`${styles.combosFilterBtn} ${filterStatus === 'inactive' ? styles.combosFilterBtnActive : ''}`}
            onClick={() => setFilterStatus('inactive')}
          >
            Inativos
          </button>
        </div>
      )}

      {isLoading ? (
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner} />
          <span>Carregando combos...</span>
        </div>
      ) : combos.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <Icon icon="mdi:package-variant" />
          </div>
          <h4>Nenhum combo cadastrado</h4>
          <p>Crie combos promocionais para aumentar as vendas</p>
          {canEdit && (
            <button className={styles.addComboBtn} type="button" onClick={handleOpenCreateModal}>
              <Icon icon="mdi:plus" />
              Criar Combo
            </button>
          )}
        </div>
      ) : filteredCombos.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <Icon icon="mdi:filter-off-outline" />
          </div>
          <h4>Nenhum combo encontrado</h4>
          <p>
            {filterStatus === 'active'
              ? 'Não há combos ativos. Altere o filtro ou reative um combo.'
              : 'Não há combos inativos. Altere o filtro.'}
          </p>
        </div>
      ) : (
        <div className={styles.combosGrid}>
          {filteredCombos.map((combo) => {
            const fullPriceForDisplay = calculateComboFullPriceFromCampaignPrice(
              campaignPrice,
              combo.quantity,
              combo.full_price
            )
            const comboEmbedSnippet = buildComboOnlyWidgetSnippet(
              campaignId,
              combo.id,
              combo.cta_text,
              snippetFields,
              snippetLandingColor,
            )

            return (
              <div
                key={combo.id}
                className={`${styles.comboCard} ${!combo.is_active ? styles.comboCardInactive : ''}`}
              >
                <div className={styles.comboCardHeader}>
                  <div className={styles.comboHeaderMain}>
                    <div className={styles.comboTitleRow}>
                      <span
                        className={styles.comboIdBadge}
                        title={`ID do combo: ${combo.id}`}
                        aria-label={`Identificador do combo ${combo.id}`}
                      >
                        {combo.id}
                      </span>
                      <h4 className={styles.comboTitle}>{combo.title}</h4>
                    </div>
                    <div className={styles.comboMetaRow}>
                      <span className={`${styles.comboStatus} ${combo.is_active ? styles.comboStatusActive : styles.comboStatusInactive}`}>
                        <Icon icon={combo.is_active ? 'mdi:check-circle' : 'mdi:close-circle'} />
                        {combo.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                      <span className={styles.comboPositionBadge}>Posição {combo.position}</span>
                      <span className={`${styles.comboStatus} ${combo.is_visible ? styles.comboStatusVisible : styles.comboStatusHidden}`}>
                        <Icon icon={combo.is_visible ? 'mdi:eye-outline' : 'mdi:eye-off-outline'} />
                        {combo.is_visible ? 'Visível' : 'Oculto'}
                      </span>
                      {combo.is_favorite && (
                        <span className={`${styles.comboStatus} ${styles.comboStatusFavorite}`}>
                          <Icon icon="mdi:star" />
                          Favorito
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className={styles.comboCardBody}>
                  {combo.description && <p className={styles.comboDescription}>{combo.description}</p>}

                  <div className={styles.comboStats}>
                    <div className={styles.comboStat}>
                      <span className={styles.comboStatValue}>{combo.quantity}</span>
                      <span className={styles.comboStatLabel}>Números</span>
                    </div>
                    <div className={styles.comboStat}>
                      <span className={styles.comboStatValue}>{formatComboPrice(Math.abs(combo.savings))}</span>
                      <span className={styles.comboStatLabel}>
                        {combo.savings >= 0 ? 'Economia' : 'Acréscimo'}
                      </span>
                    </div>
                    <div className={styles.comboStat}>
                      <span className={styles.comboStatValue}>
                        {combo.discount_percentage >= 0
                          ? `${combo.discount_percentage}%`
                          : `${Math.abs(combo.discount_percentage)}%`}
                      </span>
                      <span className={styles.comboStatLabel}>
                        {combo.discount_percentage >= 0 ? 'Desconto' : 'Acréscimo'}
                      </span>
                    </div>
                  </div>

                  <div className={styles.comboPricing}>
                    <div className={styles.comboPriceInfo}>
                      <span className={styles.comboFullPrice}>De: {formatComboPrice(fullPriceForDisplay)}</span>
                      <span className={styles.comboPrice}>Por: {formatComboPrice(combo.combo_price)}</span>
                    </div>
                    <span className={styles.comboDiscount}>
                      <Icon icon="mdi:tag" />
                      {combo.discount_percentage >= 0
                        ? `${combo.discount_percentage}% OFF`
                        : `${Math.abs(combo.discount_percentage)}% acréscimo`}
                    </span>
                  </div>
                </div>

                {canEdit && (
                  <>
                    <div className={styles.quickActions}>
                      <button
                        className={styles.quickActionBtn}
                        type="button"
                        onClick={() =>
                          handlePatchCombo(
                            combo.id,
                            { is_visible: !combo.is_visible },
                            'Não foi possível atualizar a visibilidade do combo.'
                          )
                        }
                      >
                        <Icon icon={combo.is_visible ? 'mdi:eye-off-outline' : 'mdi:eye-outline'} />
                        <span>{combo.is_visible ? 'Ocultar' : 'Exibir'}</span>
                      </button>
                      <button
                        className={styles.quickActionBtn}
                        type="button"
                        onClick={() =>
                          handlePatchCombo(
                            combo.id,
                            { is_favorite: !combo.is_favorite },
                            'Não foi possível atualizar o favorito do combo.'
                          )
                        }
                      >
                        <Icon icon={combo.is_favorite ? 'mdi:star-off-outline' : 'mdi:star-outline'} />
                        <span>{combo.is_favorite ? 'Desfavoritar' : 'Favoritar'}</span>
                      </button>
                    </div>

                    <div className={styles.comboActions}>
                      <button
                        className={styles.comboActionBtn}
                        type="button"
                        onClick={() =>
                          handlePatchCombo(
                            combo.id,
                            { is_active: !combo.is_active },
                            'Não foi possível atualizar o status do combo.'
                          )
                        }
                        title={combo.is_active ? 'Desativar combo' : 'Ativar combo'}
                      >
                        <Icon icon={combo.is_active ? 'mdi:pause-circle-outline' : 'mdi:play-circle-outline'} />
                        <span>{combo.is_active ? 'Pausar' : 'Ativar'}</span>
                      </button>
                      <button
                        className={styles.comboActionBtn}
                        type="button"
                        onClick={() => handleOpenEditModal(combo)}
                        title="Editar combo"
                      >
                        <Icon icon="mdi:pencil-outline" />
                        <span>Editar</span>
                      </button>
                      <button
                        className={`${styles.comboActionBtn} ${styles.comboActionBtnDelete}`}
                        type="button"
                        onClick={() => handleDelete(combo.id)}
                        title="Excluir combo"
                      >
                        <Icon icon="mdi:delete-outline" />
                        <span>Excluir</span>
                      </button>
                    </div>

                    {combo.is_active && combo.is_visible && (
                      <div className={styles.comboEmbedBlock}>
                        <button
                          type="button"
                          className={`${styles.comboEmbedScriptBtn} ${copiedComboId === combo.id ? styles.comboEmbedScriptBtnSuccess : ''}`}
                          onClick={() => {
                            void navigator.clipboard
                              .writeText(comboEmbedSnippet)
                              .then(() => {
                                setError(null)
                                setCopiedComboId(combo.id)
                                window.setTimeout(() => setCopiedComboId(null), 2000)
                              })
                              .catch(() => {
                                setError(
                                  'Não foi possível copiar. Confirme que o painel está em HTTPS e que o navegador permite acesso à área de transferência.',
                                )
                              })
                          }}
                          aria-label={`Copiar script de incorporação do combo ${combo.title}`}
                        >
                          <Icon icon={copiedComboId === combo.id ? 'mdi:check-circle' : 'mdi:script-text-outline'} aria-hidden />
                          {copiedComboId === combo.id ? 'Copiado' : 'Copiar Script do Combo'}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}

      {isModalOpen &&
        createPortal(
          <ComboModal
            combo={editingCombo}
            campaignPrice={campaignPrice}
            isSubmitting={isSubmitting}
            onClose={handleCloseModal}
            onSubmit={handleSubmit}
          />,
          document.body
        )}
    </section>
  )
}

interface ComboModalProps {
  combo: Combo | null
  campaignPrice?: number | string | null
  isSubmitting: boolean
  onClose: () => void
  onSubmit: (data: ComboInput) => Promise<void>
}

function parseDecimalValue(value: number | string | null | undefined): number {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0
  }

  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value.replace(',', '.').trim())
    return Number.isFinite(parsed) ? parsed : 0
  }

  return 0
}

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100
}

function ComboModal({
  combo,
  campaignPrice,
  isSubmitting,
  onClose,
  onSubmit,
}: ComboModalProps) {
  const [title, setTitle] = useState(combo?.title || '')
  const [description, setDescription] = useState(combo?.description || '')
  const [quantity, setQuantity] = useState(combo?.quantity?.toString() || '10')
  const [position, setPosition] = useState(combo?.position?.toString() || combo?.sort_order?.toString() || '0')
  const [comboPriceValue, setComboPriceValue] = useState(() => {
    if (combo?.combo_price != null) return combo.combo_price.toString()
    return ''
  })
  const [ctaText, setCtaText] = useState(combo?.cta_text || 'Adicionar combo')
  const [isActive, setIsActive] = useState(combo?.is_active ?? true)
  const [isVisible, setIsVisible] = useState(combo?.is_visible ?? true)
  const [isFavorite, setIsFavorite] = useState(combo?.is_favorite ?? false)

  const pricePerNumber = parseDecimalValue(campaignPrice)

  const quantityNum = parseInt(quantity) || 0
  const positionNum = Math.max(0, parseInt(position) || 0)
  const comboPriceNum = parseDecimalValue(comboPriceValue)
  const fullPrice = roundMoney(pricePerNumber * quantityNum)

  const discountNum =
    fullPrice > 0 && comboPriceNum >= 0
      ? Math.round(((fullPrice - comboPriceNum) / fullPrice) * 10000) / 100
      : 0

  const savings = fullPrice > 0 && comboPriceNum >= 0 ? roundMoney(fullPrice - comboPriceNum) : 0
  const previewComboPrice =
    fullPrice > 0 && comboPriceNum >= 0
      ? roundMoney(comboPriceNum)
      : roundMoney(fullPrice * (1 - discountNum / 100))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    void onSubmit({
      title,
      description: description || null,
      quantity: quantityNum,
      combo_price: roundMoney(comboPriceNum),
      cta_text: ctaText,
      is_active: isActive,
      is_visible: isVisible,
      is_favorite: isFavorite,
      position: positionNum,
    })
  }

  return (
    <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="combo-modal-title">
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h3 id="combo-modal-title" className={styles.modalTitle}>
            {combo ? 'Editar Combo' : 'Novo Combo'}
          </h3>
        </div>

        <form onSubmit={handleSubmit}>
          <div className={styles.modalBody}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="combo-title">
                Título *
              </label>
              <input
                id="combo-title"
                type="text"
                className={styles.formInput}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex: Combo 10 Números"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="combo-description">
                Descrição
              </label>
              <textarea
                id="combo-description"
                className={styles.formTextarea}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ex: Leve 10 números com 20% de desconto"
              />
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="combo-position">
                  Posição
                </label>
                <input
                  id="combo-position"
                  type="number"
                  min="0"
                  className={styles.formInput}
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                />
                <span className={styles.formHint}>Controla a ordem do combo nas listagens</span>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="combo-quantity">
                  Quantidade de Números *
                </label>
                <input
                  id="combo-quantity"
                  type="number"
                  className={styles.formInput}
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  min="1"
                  required
                />
                <span className={styles.formHint}>Quantos números o combo inclui</span>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="combo-value">
                Valor (R$)
              </label>
              <CurrencyInput
                id="combo-value"
                className={styles.formInput}
                value={comboPriceValue || undefined}
                onChange={(value) => setComboPriceValue(value)}
                placeholder={fullPrice > 0 ? formatComboPrice(fullPrice * 0.8) : 'R$ 0,00'}
                disabled={fullPrice <= 0}
              />
              <span className={styles.formHint}>
                {fullPrice > 0
                  ? `Preço final do combo. ${discountNum >= 0 ? 'Desconto' : 'Acréscimo'} calculado: ${discountNum >= 0 ? discountNum.toFixed(1) : Math.abs(discountNum).toFixed(1)}%`
                  : 'Defina o preço da campanha para habilitar o valor do combo'}
              </span>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="combo-cta">
                Texto do Botão
              </label>
              <input
                id="combo-cta"
                type="text"
                className={styles.formInput}
                value={ctaText}
                onChange={(e) => setCtaText(e.target.value)}
                placeholder="Ex: Adicionar combo"
              />
            </div>

            <div className={styles.formChecklist}>
              <label className={styles.formCheckbox}>
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                <span>Combo ativo</span>
              </label>
              <label className={styles.formCheckbox}>
                <input type="checkbox" checked={isVisible} onChange={(e) => setIsVisible(e.target.checked)} />
                <span>Combo visível</span>
              </label>
              <label className={styles.formCheckbox}>
                <input type="checkbox" checked={isFavorite} onChange={(e) => setIsFavorite(e.target.checked)} />
                <span>Combo favorito</span>
              </label>
            </div>

            {pricePerNumber > 0 && quantityNum > 0 && (
              <div className={styles.comboPreviewCard}>
                <strong className={styles.comboPreviewTitle}>Prévia do Combo</strong>
                <div className={styles.comboPreviewRow}>
                  <span>Preço original:</span>
                  <span className={styles.comboPreviewMuted}>{formatComboPrice(fullPrice)}</span>
                </div>
                <div className={styles.comboPreviewRow}>
                  <span>Valor final:</span>
                  <strong className={styles.comboPreviewHighlight}>{formatComboPrice(previewComboPrice)}</strong>
                </div>
                <div className={styles.comboPreviewRow}>
                  <span>{discountNum >= 0 ? 'Desconto:' : 'Acréscimo:'}</span>
                  <span className={discountNum >= 0 ? styles.comboPreviewPositive : styles.comboPreviewNegative}>
                    {discountNum >= 0 ? `${discountNum.toFixed(1)}%` : `${Math.abs(discountNum).toFixed(1)}%`}
                  </span>
                </div>
                <div className={styles.comboPreviewRow}>
                  <span>Economia:</span>
                  <span className={styles.comboPreviewPositive}>{formatComboPrice(savings)}</span>
                </div>
              </div>
            )}
          </div>

          <div className={styles.modalFooter}>
            <button className={styles.btnCancel} type="button" onClick={onClose}>
              Cancelar
            </button>
            <button
              className={styles.btnSubmit}
              type="submit"
              disabled={isSubmitting || !title || quantityNum < 1 || (fullPrice > 0 && comboPriceNum <= 0)}
            >
              {isSubmitting ? 'Salvando...' : combo ? 'Salvar' : 'Criar Combo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
